import { on, removeListener, run } from '../helper/electron';
import AppleMusicService from './AppleMusic.service';
import SpotifyService from './Spotify.service';
import TidalService from './Tidal.service';
import {
  createAgentMyCrate,
  getCrateHackersCrate,
  searchCrateHackersCrates,
  type AgentCrateTrackInput,
} from './AgentCatalog.service';
import { getTrackRecommendations } from './AgentRecommendations.service';
import { isAgentSourceIncluded } from './agentSourceIncludesState';
import { listStreamingConnections } from '../hooks/useStreamingConnections';
import { iTrack } from '../types/ITrack';
import { publishAgentTransfer } from './AgentTransferFeed.service';
import { exportTracksToStreaming } from './StreamingExport.service';
import { SERVICE_NAMES, StreamingServiceId } from '../types/Streaming';

const INVOKE = 'agent/streaming-invoke';
const RESULT = 'agent/streaming-result';

/** The agent cannot be allowed to spend an hour matching a whole library. */
const MAX_EXPORT_TRACKS = 200;

type StreamingService = StreamingServiceId;

type InvokePayload = {
  requestId: string;
  action: string;
  args?: Record<string, unknown>;
};

type SpotifyPlaylistItem = {
  id: string;
  name: string;
  tracks?: { total?: number };
};

type TidalPlaylistItem = {
  id: string;
  type?: string;
  attributes?: { name?: string; numberOfItems?: number };
};

const summarizeTrack = (track: iTrack) => ({
  id: track.ID,
  artist: track.Artist,
  title: track.Title,
  album: track.Album ?? null,
  isrc: track.isrc ?? null,
});

const summarizeSpotifyPlaylist = (item: SpotifyPlaylistItem) => ({
  id: item.id,
  name: item.name,
  trackCount: item.tracks?.total ?? 0,
});

const summarizeTidalPlaylist = (item: TidalPlaylistItem) => ({
  id: item.id,
  name: item.attributes?.name ?? 'Untitled',
  trackCount: item.attributes?.numberOfItems ?? 0,
});

const asService = (value: unknown): StreamingService | null =>
  value === 'spotify' || value === 'tidal' || value === 'apple' ? value : null;

const unavailable = (service: string) => ({
  available: false,
  message: `${service} is not connected. Ask the user to connect it from the @ button in the chat box.`,
});

const isConnected: Record<StreamingService, () => boolean> = {
  spotify: () => !!SpotifyService.getAccessToken(),
  tidal: () => TidalService.isConnected(),
  apple: () => AppleMusicService.isAuthorized(),
};

/** Attached to this chat and signed in, or the reason it cannot be used. */
const guard = (service: StreamingService) => {
  if (!isAgentSourceIncluded(service)) {
    return {
      available: false,
      message: `${SERVICE_NAMES[service]} is connected but not attached to this chat. Ask the user to add it with the @ button in the chat box.`,
    };
  }
  if (!isConnected[service]()) return unavailable(SERVICE_NAMES[service]);
  return null;
};

const listPlaylists = async (service: StreamingService) => {
  const skip = guard(service);
  if (skip) return skip;

  if (service === 'spotify') {
    const response = await SpotifyService.getPlaylists();
    const items = Array.isArray(response?.items) ? response.items : [];
    return {
      available: true,
      playlists: items.slice(0, 50).map(summarizeSpotifyPlaylist),
    };
  }
  if (service === 'tidal') {
    const response = await TidalService.getPlaylists();
    const raw = response?.included || response?.data || [];
    const playlists = (Array.isArray(raw) ? raw : [])
      .filter((item: TidalPlaylistItem) => item.type === 'playlists')
      .slice(0, 50)
      .map(summarizeTidalPlaylist);
    return { available: true, playlists };
  }
  const playlists = await AppleMusicService.getPlaylists();
  return {
    available: true,
    playlists: playlists.slice(0, 50).map((item) => ({
      id: item.id,
      name: item.name,
      trackCount: item.trackCount ?? 0,
    })),
  };
};

const searchTracks = async (service: StreamingService, query: string) => {
  const skip = guard(service);
  if (skip) return skip;

  if (service === 'spotify') {
    const response = await SpotifyService.SearchTracks(query);
    const tracks = (response?.tracks ?? []).slice(0, 20).map(summarizeTrack);
    return { available: true, tracks };
  }
  if (service === 'tidal') {
    const tracks = (await TidalService.searchTracks(query))
      .slice(0, 20)
      .map(summarizeTrack);
    return { available: true, tracks };
  }
  const tracks = (await AppleMusicService.searchTracks(query))
    .slice(0, 20)
    .map(summarizeTrack);
  return { available: true, tracks };
};

const playlistTracks = async (
  service: StreamingService,
  playlistId: string,
) => {
  const skip = guard(service);
  if (skip) return skip;

  if (service === 'spotify') {
    const tracks = (await SpotifyService.getPlaylistTracks(playlistId))
      .slice(0, 50)
      .map(summarizeTrack);
    return { available: true, tracks };
  }
  if (service === 'tidal') {
    const tracks = (await TidalService.getPlaylistTracksAsITrack(playlistId))
      .slice(0, 50)
      .map(summarizeTrack);
    return { available: true, tracks };
  }
  const tracks = (await AppleMusicService.getPlaylistTracks(playlistId))
    .slice(0, 50)
    .map(summarizeTrack);
  return { available: true, tracks };
};

/** Tracks arrive from the agent as plain metadata; the matcher needs iTracks. */
const toRequestedTracks = (value: unknown): iTrack[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index) => {
      const row = entry as Record<string, unknown>;
      const artist = typeof row?.artist === 'string' ? row.artist.trim() : '';
      const title = typeof row?.title === 'string' ? row.title.trim() : '';
      if (!title) return null;
      return {
        ID: `agent-${index}`,
        Artist: artist,
        Title: title,
        isrc: typeof row?.isrc === 'string' ? row.isrc : undefined,
      } as iTrack;
    })
    .filter((track): track is iTrack => track !== null);
};

const createPlaylist = async (
  service: StreamingService,
  args: Record<string, unknown>,
) => {
  const skip = guard(service);
  if (skip) return skip;

  const playlistName = typeof args.name === 'string' ? args.name.trim() : '';
  if (!playlistName) {
    return { available: false, message: 'name is required.' };
  }

  const tracks = toRequestedTracks(args.tracks);
  if (!tracks.length) {
    return {
      available: false,
      message: 'tracks is required, with an artist and title on each entry.',
    };
  }
  if (tracks.length > MAX_EXPORT_TRACKS) {
    return {
      available: false,
      message: `That is ${tracks.length} tracks. Export at most ${MAX_EXPORT_TRACKS} at a time.`,
    };
  }

  const result = await exportTracksToStreaming(
    {
      service,
      playlistName,
      description:
        typeof args.description === 'string' ? args.description : undefined,
      tracks,
    },
    publishAgentTransfer,
  );

  return {
    available: true,
    service,
    playlistId: result.playlistId,
    playlistName,
    addedCount: result.matched.length,
    requestedCount: tracks.length,
    // Only the misses matter in the reply; the agent already knows what it sent.
    unmatched: result.unmatched,
  };
};

const handle = async (
  action: string,
  args: Record<string, unknown> = {},
): Promise<unknown> => {
  if (action === 'connections') {
    return {
      services: listStreamingConnections({
        spotify: !!SpotifyService.getAccessToken(),
        tidal: TidalService.isConnected(),
        apple: AppleMusicService.isAuthorized(),
      }).map((service) => ({
        ...service,
        included: isAgentSourceIncluded(service.id),
      })),
    };
  }

  if (action === 'search_crates') {
    const query = typeof args.query === 'string' ? args.query : '';
    return searchCrateHackersCrates(query);
  }
  if (action === 'get_crate') {
    const crateId = typeof args.crateId === 'string' ? args.crateId.trim() : '';
    if (!crateId) return { available: false, message: 'crateId is required.' };
    return getCrateHackersCrate(crateId);
  }
  if (action === 'create_my_crate') {
    const title = typeof args.title === 'string' ? args.title.trim() : '';
    const sourceCrateId =
      typeof args.sourceCrateId === 'string' ? args.sourceCrateId.trim() : '';
    const description =
      typeof args.description === 'string'
        ? args.description.trim()
        : undefined;
    const tracks = Array.isArray(args.tracks)
      ? (args.tracks as AgentCrateTrackInput[])
      : undefined;
    const mixableOrder =
      args.mixableOrder === false
        ? false
        : args.mixableOrder === true || (tracks != null && tracks.length > 0);
    return createAgentMyCrate({
      title,
      description,
      sourceCrateId: sourceCrateId || undefined,
      tracks,
      mixableOrder,
    });
  }
  if (action === 'track_recommendations') {
    const term = typeof args.term === 'string' ? args.term.trim() : '';
    if (!term) return { available: false, message: 'term is required.' };
    return getTrackRecommendations(term);
  }

  const service = asService(args.service);
  if (!service) {
    return {
      available: false,
      message: 'service must be spotify, tidal or apple.',
    };
  }

  if (action === 'playlists') return listPlaylists(service);
  if (action === 'search') {
    const query = typeof args.query === 'string' ? args.query.trim() : '';
    if (!query) return { available: false, message: 'query is required.' };
    return searchTracks(service, query);
  }
  if (action === 'playlist_tracks') {
    const playlistId =
      typeof args.playlistId === 'string' ? args.playlistId.trim() : '';
    if (!playlistId) {
      return { available: false, message: 'playlistId is required.' };
    }
    return playlistTracks(service, playlistId);
  }
  if (action === 'create_playlist') return createPlaylist(service, args);

  return { available: false, message: `Unknown streaming action: ${action}` };
};

export const startAgentStreamingBridge = (): (() => void) => {
  const listener = async (payload: InvokePayload) => {
    if (!payload?.requestId) return;
    try {
      const result = await handle(payload.action, payload.args);
      run(RESULT, { requestId: payload.requestId, result });
    } catch (error) {
      run(RESULT, {
        requestId: payload.requestId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  on(INVOKE, listener);
  return () => removeListener(INVOKE, listener);
};
